import { useEffect, useRef } from "react";
import { Link } from 'react-router-dom';
import { FaMoon } from "react-icons/fa";
import { GridStack } from "gridstack";
import Weather from "../components/Weather";
import usePageTitle from "../hooks/usePageTitle";
import aiSlide from "../assets/AI-slide.png";
import StandardLink from "../tools/StandardLink";

import "gridstack/dist/gridstack.min.css";
import "../styles/exploration.scss";

function PuzzleGrid({ gridRef }) {
  return (
    <div className="grid-stack" ref={gridRef}>
      {/* Row 0 */}
      <div className="grid-stack-item" gs-x="2" gs-y="0" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>
      <div className="grid-stack-item" gs-x="3" gs-y="0" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>
      <div className="grid-stack-item" gs-x="4" gs-y="0" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>
      <div className="grid-stack-item" gs-x="5" gs-y="0" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>

      {/* Row 1 */}
      <div className="grid-stack-item" gs-x="1" gs-y="1" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>
      <div className="grid-stack-item" gs-x="2" gs-y="1" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>
      <div className="grid-stack-item" gs-x="3" gs-y="1" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>
      <div className="grid-stack-item" gs-x="4" gs-y="1" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>

      {/* Row 2 */}
      <div className="grid-stack-item" gs-x="3" gs-y="2" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>
      <div className="grid-stack-item" gs-x="4" gs-y="2" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>
      <div className="grid-stack-item" gs-x="5" gs-y="2" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>
      <div className="grid-stack-item" gs-x="6" gs-y="2" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>

      {/* Row 3 */}
      <div className="grid-stack-item" gs-x="2" gs-y="3" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>
      <div className="grid-stack-item" gs-x="3" gs-y="3" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>
      <div className="grid-stack-item" gs-x="4" gs-y="3" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>
      <div className="grid-stack-item" gs-x="5" gs-y="3" gs-w="1" gs-h="1">
        <div className="grid-stack-item-content tile" />
      </div>
    </div>
  );
}

export default function Exploration() {
  usePageTitle("Exploration | Dochano");
  const gridRef = useRef(null);

  useEffect(() => {
    if (!gridRef.current) return;

    let grid;
    try {
      // Init GridStack on the existing markup
      grid = GridStack.init(
        {
          column: 8,
          cellHeight: 75,
          margin: 0,
          float: true,
          disableResize: true, // ok in recent versions
        },
        gridRef.current
      );
    } catch (e) {
      console.error("GridStack init failed:", e);
      return;
    }

    // CSS variables (optional)
    const root = document.documentElement;
    root.style.setProperty("--gs-column-width", "100px");
    root.style.setProperty("--gs-cell-height", "75px");
    root.style.setProperty("--gs-item-margin-left", "0px");
    root.style.setProperty("--gs-item-margin-right", "0px");
    root.style.setProperty("--gs-item-margin-top", "0px");
    root.style.setProperty("--gs-item-margin-bottom", "0px");

    // Shuffle background positions for 4x4 puzzle
    const positions = [];
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 4; col++) {
        positions.push({ x: -100 * col, y: -75 * row });
      }
    }
    for (let i = positions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [positions[i], positions[j]] = [positions[j], positions[i]];
    }

    // Apply background tiles
    const tiles = gridRef.current.querySelectorAll(".grid-stack-item-content");
    tiles.forEach((el, i) => {
      const pos = positions[i];
      el.style.backgroundImage = `url('${aiSlide}')`;
      el.style.backgroundSize = "400px 300px";
      el.style.backgroundPosition = `${pos.x}px ${pos.y}px`;
    });

    // Cleanup without deleting DOM nodes
    return () => {
      try {
        grid?.destroy(false);
      } catch (e) {
        console.warn("GridStack destroy warning:", e);
      }
    };
  }, []);

  return (
    <section id="Exploration">
      <div id="Intro">
        <h1>Exploration</h1>
      </div>
      <p>
        This page is devoted to a test arena along with interesting sites
        related to Web Engineering.
      </p>

      <div id="css-tricks">
        <h2>CSS Tricks</h2>
        <p className="indent">
          I keep up with some site styling topics. Back in my University days as
          a computer lab tech, I would challenge myself to make any design or
          picture into a working website. This area is dedicated to fun elements
          that can make a site stand out. Inspired by{" "}
          <StandardLink href="https://codepen.io/spark" target="_blank" rel="noopener noreferrer">
            CodePen Spark
          </StandardLink>{" "}
          and{" "}
          <StandardLink href="https://css-tricks.com/" target="_blank" rel="noopener noreferrer">
            CSS Tricks
          </StandardLink>
        </p>

        <dl className="indent">
          <dt>
            Display Weather API within a sparkle-bordered Ad to catch the user’s
            eye. Click the "Embrace: <FaMoon title="Dark Mode" />" to see a
            different view.
          </dt>
          <dd>
            <Weather id="Weather" location="Denver" />
          </dd>

          <dt>
            <a
              href="https://dev.to/ivordev/triggering-a-popover-with-pure-css-no-javascript-required-12eb"
              target="_blank"
              rel="noopener noreferrer"
            >
              CSS Only Pop-up
            </a>
          </dt>
          <dd>
            Uses input checkbox style trigger handling to open. Interesting but
            could be odd for A11Y users.
          </dd>

          <dt>
            <StandardLink
              href="https://dev.to/giftintech/how-to-make-a-simple-bounce-animation-with-just-html-and-css-2lae"
              target="_blank"
              rel="noopener noreferrer"
            >
              CSS Bounce
            </StandardLink>{" "}
            <div className="bounce" />
          </dt>
          <dd>Style used to move an element from side to side or up/down.</dd>

          <dt>
            <StandardLink href="https://codepen.io/calm/pen/ZYGjJyZ" target="_blank" rel="noopener noreferrer">Moving Puzzle</StandardLink>
          </dt>
          <dd>
            4x4 grid that user can move pieces to properly view image.
            <PuzzleGrid gridRef={gridRef} />
          </dd>
        </dl>
      </div>

      <div id="python-playground">
        <h2>Python Playground</h2>
        <p className="indent">
          Be sure to checkout my public{" "}
          <a
            href="https://github.com/grjohnson1/Python_Arena"
            target="_blank"
            rel="noopener noreferrer"
          >
            GitHub python_arena
          </a>{" "}
          to see projects I’ve been working on in my spare time. Originally I
          dove into Machine Learning, Model Training, and AI. Now I am diving
          deeper into Python coding.
        </p>

        <h3>Python Standout Scripts</h3>

        <h4>mazeGenerator.py</h4>
        <p className="indent">
          Draws a maze using a grid layout. This inspired me to create a maze
          game for my website: <Link href="/Maze">Maze</Link>.
        </p>

        <h4>Dolittle.py</h4>
        <p className="indent">
          A learning script that asks questions to identify an animal you’re
          thinking of. If it fails, it asks for the animal and a unique
          question, storing it for future attempts. Over time, it learns
          iteratively — showing how knowledge can grow without a massive data
          lake.
        </p>

        <h4>pdf_keyword_search.py</h4>
        <p className="indent">
          Searches PDFs in a folder for given keywords and generates a CSV with
          results. This was personally created outside of textbook examples.
        </p>

        <h4>
          <a
            href="https://github.com/grjohnson1/Python_Arena/tree/main/AI_InEasySteps/FlatworldGame"
            target="_blank"
            rel="noopener noreferrer"
          >
            Flatworld Game
          </a>
        </h4>
        <p className="indent">
          Visual game where projects build off each other. Best to see
          screenshots and read more from the{" "}
          <a
            href="https://github.com/grjohnson1/Python_Arena/tree/main/AI_InEasySteps/FlatworldGame"
            target="_blank"
            rel="noopener noreferrer"
          >
            GitHub Repo Readme file
          </a>{" "}
          directly.
        </p>
      </div>
    </section>
  );
}
